/**
 * ログアウト遷移。
 *
 * `requiresCrossPlatformLogout` が true のとき cross-platform ログアウトを行う。
 * - QANT ホスト + cross-platform: 現在のタブ QANT `/auth/logout`、新規タブ KARTE `/admin/logout`
 * - KARTE ホスト + cross-platform: 現在のタブ KARTE `/admin/logout`、新規タブ QANT `/auth/logout`
 * - `requiresCrossPlatformLogout` が false: host-only（自基盤のみ）。例: 相手基盤アカウントなし、
 *   QANT ホストで KARTE アカウントはあるが `hasAnyQantProductAccess === false`
 *
 * KARTE セッション Cookie (`krtssid`) は SameSite=Lax のため、新規タブでのトップレベル GET
 * 遷移では Cookie が送出される。docs/logout-problem/logout-experiment-cross-tab.md を参照。
 */

import type { GlobalNaviLinkedAccounts, Platform } from '../contract/index.js';
import { AUTH_ROUTE_TEMPLATES, resolveAuthHref } from './authRoutes.js';
import { AUTO_SSO_PARAM, type HrefBuilder } from './urls.js';

/** KARTE 基盤のログアウト endpoint（メソッド非依存・CSRF 検証オフ・セッションなしでも no-op）。 */
export const KARTE_LOGOUT_PATH = '/admin/logout';

export type SingleSignOutTargets = {
  /** 現在のタブを遷移させる QANT ログアウト URL */
  qantLogoutHref: string;
  /** 新規タブで開く KARTE ログアウト URL */
  karteLogoutHref: string;
};

/**
 * `?sso=qant`（自動 SSO トリガー）を落とす。`createHrefBuilder` は QANT 基盤発の karte-io 向け
 * cross-domain リンク全部にこのパラメータを付けるため、ログアウト URL に残すと KARTE が
 * ログアウト直後に OIDC を自動開始し再ログインしてしまう。
 */
const stripAutoSsoParam = (href: string): string => {
  if (!/^https?:\/\//.test(href)) return href;
  const url = new URL(href);
  url.searchParams.delete(AUTO_SSO_PARAM.name);
  return url.toString();
};

export const buildSingleSignOutTargets = (builder: HrefBuilder): SingleSignOutTargets => {
  const qant = resolveAuthHref(AUTH_ROUTE_TEMPLATES.logout, builder);
  const karte = builder({ host: 'karte-io', path: KARTE_LOGOUT_PATH, skipProjectParam: true });
  return { qantLogoutHref: qant.href, karteLogoutHref: stripAutoSsoParam(karte.href) };
};

export type CrossPlatformLogoutResult = 'ok' | 'other-tab-blocked';

export type RunLogoutResult = CrossPlatformLogoutResult | 'host-only';

/** `runSingleSignOut` / `runLogout` が使う window の最小インターフェース（テストで差し替える）。 */
export type SingleSignOutWindow = {
  open: (url: string, target: string) => unknown;
  location: { assign: (url: string) => void };
};

export const hasOtherPlatformLinkedAccount = (selfPlatform: Platform, linkedAccounts: GlobalNaviLinkedAccounts): boolean =>
  selfPlatform === 'karte' ? linkedAccounts.qant : linkedAccounts.karte;

/**
 * 実際の遷移を行う。クリックハンドラから同期的に呼ぶこと（`window.open` が非同期の文脈だと
 * ポップアップブロックの対象になる）。
 *
 * QANT ホスト向け cross-platform ログアウト。KARTE ホストでは `runLogout` を使う。
 */
export const runSingleSignOut = (
  targets: SingleSignOutTargets,
  win: SingleSignOutWindow,
  onBlocked?: (href: string) => void,
): CrossPlatformLogoutResult => {
  const opened = win.open(targets.karteLogoutHref, '_blank');
  if (!opened) onBlocked?.(targets.karteLogoutHref);
  win.location.assign(targets.qantLogoutHref);
  return opened ? 'ok' : 'other-tab-blocked';
};

/**
 * `requiresCrossPlatformLogout` が true なら cross-platform、false なら自基盤のみログアウトする。
 * フラグは `GlobalNaviDataV2`（`buildGlobalNaviDataV2`）で決まる。本関数は `linkedAccounts` を見ない。
 */
export const runLogout = (
  params: {
    selfPlatform: Platform;
    requiresCrossPlatformLogout: boolean;
    buildHref: HrefBuilder;
  },
  win: SingleSignOutWindow,
  onBlocked?: (href: string) => void,
): RunLogoutResult => {
  const targets = buildSingleSignOutTargets(params.buildHref);
  if (!params.requiresCrossPlatformLogout) {
    const selfHref = params.selfPlatform === 'karte' ? targets.karteLogoutHref : targets.qantLogoutHref;
    win.location.assign(selfHref);
    return 'host-only';
  }

  if (params.selfPlatform === 'karte') {
    const opened = win.open(targets.qantLogoutHref, '_blank');
    if (!opened) onBlocked?.(targets.qantLogoutHref);
    win.location.assign(targets.karteLogoutHref);
    return opened ? 'ok' : 'other-tab-blocked';
  }

  return runSingleSignOut(targets, win, onBlocked);
};
